import { RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import { Alert, Button, Spinner } from '@/components/ui'

import { useChannelPairing } from '../useChannelPairing'
import { formatCountdown, useQrCountdown } from '../useQrCountdown'
import { QR_ASSUMED_TTL_SECONDS, toQrImageSrc } from '../wizardSteps'

export interface ChannelPairingCardProps {
  readonly tenantId: string
  /** `undefined` enquanto a wizard nao consegue resolver o canal (ver `deriveChannelId`). */
  readonly channelId: string | undefined
}

function QrFrame({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex h-60 w-60 items-center justify-center rounded-2xl border border-border-default bg-background-elevated p-4">
      {children}
    </div>
  )
}

/**
 * QR/pairing do WhatsApp para a tela "Conecte seu WhatsApp". O QR (`pairing.qrCodeBase64`) so
 * existe em memoria, dentro deste componente e do cache do TanStack Query: nunca e logado, nunca
 * passa por `console.*`, nunca e persistido (docs/06 secao 4, CLAUDE.md).
 */
export function ChannelPairingCard({ tenantId, channelId }: ChannelPairingCardProps) {
  const { t } = useTranslation()
  const pairingQuery = useChannelPairing(tenantId, channelId)
  const pairing = pairingQuery.data
  const countdown = useQrCountdown(pairing?.obtainedAt ?? null, QR_ASSUMED_TTL_SECONDS)

  if (channelId === undefined) {
    return (
      <div className="flex flex-col items-center gap-3 text-center">
        <QrFrame>
          <Spinner label={t('loading')} />
        </QrFrame>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-foreground-primary">
            {t('onboarding.connect.qr.unavailableTitle')}
          </p>
          <p className="max-w-xs text-sm text-foreground-muted">
            {t('onboarding.connect.qr.unavailableDescription')}
          </p>
        </div>
      </div>
    )
  }

  if (pairingQuery.isError) {
    const [message] = describeApiError(pairingQuery.error)

    return (
      <div className="flex flex-col items-center gap-3 text-center">
        <Alert variant="danger" title={t('onboarding.connect.qr.errorTitle')}>
          {message ?? t('onboarding.connect.qr.errorDescription')}
        </Alert>
        <Button
          variant="secondary"
          onClick={() => {
            void pairingQuery.refetch()
          }}
          loading={pairingQuery.isRefetching}
        >
          {t('retry')}
        </Button>
      </div>
    )
  }

  if (pairingQuery.isPending || pairing === undefined) {
    return (
      <div className="flex flex-col items-center gap-3 text-center">
        <QrFrame>
          <Spinner label={t('onboarding.connect.qr.preparingTitle')} />
        </QrFrame>
        <p className="text-sm text-foreground-muted">
          {t('onboarding.connect.qr.preparingDescription')}
        </p>
      </div>
    )
  }

  if (pairing.status === 'Connected') {
    return (
      <div className="flex flex-col items-center gap-2 text-center">
        <QrFrame>
          <span
            aria-hidden="true"
            className="flex h-16 w-16 items-center justify-center rounded-full bg-status-success-subtle text-2xl text-status-success"
          >
            &#10003;
          </span>
        </QrFrame>
        <p className="text-sm font-semibold text-status-success">
          {t('onboarding.connect.qr.connectedTitle')}
        </p>
        <p className="text-sm text-foreground-muted">
          {t('onboarding.connect.qr.connectedDescription')}
        </p>
      </div>
    )
  }

  if (pairing.status !== 'WaitingForPairing' || pairing.qrCodeBase64 === null) {
    return (
      <div className="flex flex-col items-center gap-3 text-center">
        <QrFrame>
          <Spinner label={t('onboarding.connect.qr.preparingTitle')} />
        </QrFrame>
        <p className="text-sm text-foreground-muted">
          {t('onboarding.connect.qr.preparingDescription')}
        </p>
      </div>
    )
  }

  if (countdown.expired) {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <QrFrame>
          <div className="flex flex-col items-center gap-2">
            <RefreshCw aria-hidden="true" className="h-6 w-6 text-foreground-muted" />
            <span className="text-sm font-semibold text-foreground-secondary">
              {t('onboarding.connect.qr.expiredTitle')}
            </span>
          </div>
        </QrFrame>
        <p className="max-w-xs text-sm text-foreground-muted">
          {t('onboarding.connect.qr.expiredDescription')}
        </p>
        <Button
          onClick={() => {
            void pairingQuery.refetch()
          }}
          loading={pairingQuery.isRefetching}
        >
          {t('onboarding.connect.qr.refresh')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <QrFrame>
        <img
          src={toQrImageSrc(pairing.qrCodeBase64)}
          alt={t('onboarding.connect.qr.imageAlt')}
          className="h-full w-full object-contain"
        />
      </QrFrame>
      <div className="flex items-center gap-2 text-sm text-foreground-secondary">
        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-status-warning" />
        <span>
          {t('onboarding.connect.qr.expiresIn', { time: formatCountdown(countdown.secondsLeft) })}
        </span>
      </div>
      {pairing.pairingCode !== null ? (
        <p className="text-xs text-foreground-muted">
          {t('onboarding.connect.qr.pairingCodeLabel')}:{' '}
          <code className="font-mono font-semibold text-foreground-secondary">
            {pairing.pairingCode}
          </code>
        </p>
      ) : null}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          void pairingQuery.refetch()
        }}
        loading={pairingQuery.isRefetching}
      >
        {t('onboarding.connect.qr.refresh')}
      </Button>
    </div>
  )
}
